import React, { useState, useEffect } from 'react';
import { Bell, X, BellOff } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';
import './PushPrompt.css';

const STORAGE_KEY   = 'ignite-push-prompt';
const DISMISS_DAYS  = 14; // re-show after 14 days if dismissed (not denied)

/**
 * PushPrompt
 *
 * Shows a subtle banner asking the user to enable push notifications.
 * Only appears when:
 *   1. Push is supported and not yet granted/denied
 *   2. The user hasn't dismissed it recently
 *   3. triggerShow is true (parent decides the right moment)
 *
 * Props:
 *   triggerShow  boolean   - parent sets to true at the right moment
 *   onDismiss    function  - optional callback when user dismisses
 */
export default function PushPrompt({ triggerShow, onDismiss }) {
  const { isNative, isSupported, permission, isSubscribed, isLoading, subscribe, error } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isNative)                  return; // native OS manages its own UI
    if (!isSupported)              return;
    if (permission === 'granted')  return;
    if (permission === 'denied')   return;
    if (isSubscribed)              return;
    if (!triggerShow)              return;

    // Check if recently dismissed
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (stored.dismissedAt) {
        const daysSince = (Date.now() - stored.dismissedAt) / 86400000;
        if (daysSince < DISMISS_DAYS) return;
      }
    } catch { /* ignore */ }

    // Small delay so it doesn't jar on initial render
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [isNative, isSupported, permission, isSubscribed, triggerShow]);

  async function handleEnable() {
    await subscribe();
    setSuccess(true);
  }

  function handleDismiss() {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() }));
    } catch { /* ignore */ }
    onDismiss?.();
  }

  // All hooks are above — safe to early-return for render now
  if (isNative)   return null;
  if (!visible)   return null;

  return (
    <div className={`push-prompt ${success ? 'push-prompt-success' : ''}`}>
      <div className="push-prompt-icon">
        {success ? '🙏' : <Bell size={18} />}
      </div>
      <div className="push-prompt-body">
        {success ? (
          <span className="push-prompt-ok">Notifications enabled! You're all set.</span>
        ) : (
          <>
            <span className="push-prompt-title">Stay connected</span>
            <span className="push-prompt-sub">Get notified for prayers, comments &amp; connections</span>
          </>
        )}
        {error && <span className="push-prompt-error">{error}</span>}
      </div>
      {!success && (
        <div className="push-prompt-actions">
          <button className="push-prompt-enable" onClick={handleEnable} disabled={isLoading}>
            {isLoading ? <span className="push-spinner" /> : 'Enable'}
          </button>
          <button className="push-prompt-dismiss" onClick={handleDismiss} title="Not now">
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * PushSettingsToggle
 *
 * A simple toggle row for the profile/settings screen.
 */
export function PushSettingsToggle() {
  const { isNative, isSupported, permission, isSubscribed, isLoading, subscribe, unsubscribe, error } = usePushNotifications();

  if (isNative || !isSupported) return null;

  return (
    <div className="push-settings-row">
      <div className="push-settings-left">
        {isSubscribed
          ? <Bell size={17} strokeWidth={2} style={{ color: 'var(--teal)' }} />
          : <BellOff size={17} strokeWidth={2} style={{ color: 'var(--gray-3)' }} />
        }
        <div>
          <div className="push-settings-label">Push Notifications</div>
          <div className="push-settings-sub">
            {permission === 'denied'
              ? 'Blocked in device settings'
              : isSubscribed
              ? 'Active — prayers, comments & connections'
              : 'Tap to enable'
            }
          </div>
        </div>
      </div>
      {permission !== 'denied' && (
        <button
          className={`push-settings-toggle ${isSubscribed ? 'on' : ''}`}
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={isLoading}
        >
          {isLoading ? <span className="push-spinner small" /> : isSubscribed ? 'On' : 'Off'}
        </button>
      )}
      {error && <div className="push-settings-error">{error}</div>}
    </div>
  );
}
